import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook pour gérer le cache des données avec stratégie stale-while-revalidate
 * 
 * @param {string} cacheKey - Clé unique pour le cache (ex: 'planning_2026_04')
 * @param {function} fetchFn - Fonction async qui récupère les données
 * @param {object} options - Options de configuration
 * @param {number} options.maxAge - Durée max du cache en ms (défaut: 5 minutes)
 * @param {boolean} options.revalidateOnFocus - Revalider quand la fenêtre reprend le focus
 * @param {boolean} options.revalidateOnReconnect - Revalider quand on retrouve la connexion
 */
export const useCachedData = (cacheKey, fetchFn, options = {}) => {
  const {
    maxAge = 5 * 60 * 1000, // 5 minutes par défaut
    revalidateOnFocus = true,
    revalidateOnReconnect = true,
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const isMounted = useRef(true);
  const fetchInProgress = useRef(false);

  // Récupérer les données du cache
  const getCachedData = useCallback(() => {
    try {
      const cached = localStorage.getItem(`cache_${cacheKey}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        // Retourner le cache même s'il est périmé (on revalidera en arrière-plan)
        return { data, timestamp, isStale: age > maxAge };
      }
    } catch (e) {
      console.warn('Erreur lecture cache:', e);
    }
    return null;
  }, [cacheKey, maxAge]);

  // Sauvegarder les données en cache
  const setCachedData = useCallback((newData) => {
    try {
      const cacheEntry = {
        data: newData,
        timestamp: Date.now()
      };
      localStorage.setItem(`cache_${cacheKey}`, JSON.stringify(cacheEntry));
    } catch (e) {
      console.warn('Erreur écriture cache:', e);
    }
  }, [cacheKey]);

  // Fonction de fetch avec gestion du cache
  const fetchData = useCallback(async (showLoading = true) => {
    // Éviter les requêtes en doublon
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    try {
      if (showLoading) {
        setIsRevalidating(true);
      }
      
      const freshData = await fetchFn();
      
      if (isMounted.current) {
        setData(freshData);
        setCachedData(freshData);
        setLastUpdated(Date.now());
        setError(null);
      }
    } catch (e) {
      console.error('Erreur fetch:', e);
      if (isMounted.current) {
        setError(e);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setIsRevalidating(false);
      }
      fetchInProgress.current = false;
    }
  }, [fetchFn, setCachedData]);

  // Chargement initial avec cache
  useEffect(() => {
    isMounted.current = true;
    
    const cached = getCachedData();
    
    if (cached) {
      // Afficher immédiatement les données du cache
      setData(cached.data);
      setLastUpdated(cached.timestamp);
      setLoading(false);
      
      // Si le cache est périmé, revalider en arrière-plan
      if (cached.isStale) {
        fetchData(false);
      }
    } else {
      // Pas de cache, charger normalement
      fetchData(true);
    }

    return () => {
      isMounted.current = false;
    };
  }, [cacheKey]); // Recharger quand la clé change

  // Revalider quand la fenêtre reprend le focus
  useEffect(() => {
    if (!revalidateOnFocus) return;

    const handleFocus = () => {
      const cached = getCachedData();
      if (cached?.isStale) {
        fetchData(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [revalidateOnFocus, getCachedData, fetchData]);

  // Revalider quand on retrouve la connexion
  useEffect(() => {
    if (!revalidateOnReconnect) return;

    const handleOnline = () => {
      fetchData(false);
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [revalidateOnReconnect, fetchData]);

  // Fonction pour forcer le rafraîchissement
  const refresh = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  // Fonction pour invalider le cache
  const invalidateCache = useCallback(() => {
    try {
      localStorage.removeItem(`cache_${cacheKey}`);
    } catch (e) {
      console.warn('Erreur suppression cache:', e);
    }
  }, [cacheKey]);

  // Fonction pour mettre à jour les données localement (optimistic update)
  const mutate = useCallback((updater) => {
    setData(prevData => {
      const newData = typeof updater === 'function' ? updater(prevData) : updater;
      setCachedData(newData);
      return newData;
    });
  }, [setCachedData]);

  return {
    data,
    loading,
    isRevalidating,
    error,
    lastUpdated,
    refresh,
    invalidateCache,
    mutate,
    isFromCache: lastUpdated && (Date.now() - lastUpdated > 1000)
  };
};

/**
 * Hook simplifié pour le cache du planning
 */
export const usePlanningCache = (year, month, fetchMissions) => {
  const cacheKey = `planning_${year}_${month}`;
  
  return useCachedData(cacheKey, fetchMissions, {
    maxAge: 2 * 60 * 1000, // 2 minutes pour le planning (données qui changent souvent)
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  });
};

export default useCachedData;
